import { 
  useContext, 
  useEffect,
  useMemo,
  useRef, 
  useState 
} from "react"

import { Chip, Spinner } from "@heroui/react"
import { CircleStackIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid"
import { SBAccountSelector } from "./Partials"

import { 
  DataTable, 
  EllipsisDropdown, 
  EmptyPlaceholder, 
  FlyoutMenu,
  IntegrationTypeBadge, 
  MisBadge, 
  MisCountBadge, 
  Page, 
  PageHeader, 
  SiteDataMenu,
  StaffLessonsList
} from "@components"

import { 
  useLazyQuery, 
  useMutation, 
  useQuery 
} from "@apollo/client"

import { 
  GET_SITE_DATA_STAFF_SUMMARY, 
  GET_SITE_DATA_STAFF_XLS_DOWNLOAD_LINK,
  GET_STAFF,
  REFRESH_SB_ACCOUNTS_MUTATION,
} from "@graphql"

import { NotificationContext, UserContext } from "@context"

import { 
  downloadFile, 
  getGraphQLErrorMessage, 
  lastImportDuration, 
  mySqlToLocal, 
  showLoader,
  staffFullname 
} from "@helpers"

import { useColumnManager, useSetActiveSidebarLink } from "@hooks"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import { Stats } from "./Stats"

const Staff = () => {
  const { user } = useContext(UserContext)
  const { notify } = useContext(NotificationContext)
  
  const [currentStaffMember, setCurrentStaffMember] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [sbAccounts, setSbAccounts] = useState([])
  const [selectedFilters, setSelectedFilters] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showLessonsList, setShowLessonsList] = useState(false)
  const [showSBAccountSelector, setShowSBAccountSelector] = useState(false)
  const [sortDescriptor, setSortDescriptor] = useState({ column: "surname", direction: "ascending" })
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  
  useSetActiveSidebarLink("SITE_DATA_STAFF", "SITE_DATA")
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "data.staff.columns", defaultColumns);
  
  const hasFetchedSBAccounts = useRef(false);

  const getWhereConditions = () => {
    if (selectedFilters.includes("sync")) return { column: "IS_SYNC", value: true }
    if (selectedFilters.includes("custom")) return { column: "IS_SYNC", value: false }
    if (selectedFilters.includes("isTeacher")) return { column: "IS_TEACHER", value: true }
    if (selectedFilters.includes("isNotTeacher")) return { column: "IS_TEACHER", value: false }
    return null
  }

  /**
   * Queries and Mutations
   */
  const summaryData = useQuery(GET_SITE_DATA_STAFF_SUMMARY, {
    variables: {
      clientId: user.me.client.id,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const { loading, error, data, refetch, networkStatus } = useQuery(GET_STAFF, {
    variables: {
      searchTerm,
      page,
      perPage,
      clientId: user.me.client.id,
      orderBy: [
        {
          column: sortDescriptor.column.toUpperCase(),
          order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
        },
      ],
      where: getWhereConditions(),
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  // Use REFRESH_SB_ACCOUNTS_MUTATION instead of GET_SB_ACCOUNTS to ensure we get the latest data
  const [refreshAccounts, {loading: isRefreshingAccounts }] = useMutation(REFRESH_SB_ACCOUNTS_MUTATION, {
    variables: { input: { client_id: user.me.client.id } },
    fetchPolicy: "network-only",
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while retrieving SB accounts."), open: true }),
    onCompleted: (data) => setSbAccounts(data?.refreshSBAccounts || []),
  })

  const [getSiteDataStaffXlsDownloadLink] = useLazyQuery(GET_SITE_DATA_STAFF_XLS_DOWNLOAD_LINK, {
    variables: { clientId: user.me.client.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.dataStaffXlsDownload.filename, data.dataStaffXlsDownload.url)
      setIsDownloading(false)
    },
  })

  /**
   * Memoized Values
   */
  const sbAccountMap = useMemo(() => {
    return new Map(sbAccounts.map(acc => [acc.email, acc.email]));
  }, [sbAccounts])

  /**
   * Effects
   */
  useEffect(() => {
    // Check if staff query has finished loading before firing the mutation
    if (!loading && data && !hasFetchedSBAccounts.current) {
      refreshAccounts();
      hasFetchedSBAccounts.current = true;
    }
  }, [refreshAccounts, loading, data]);

  /**
   * Functions
   */
  const getSBAccountEmail = (email) => {
    if (!email) return null
    
    return sbAccountMap.has(email) ? email : null
  }

  const getSBAccountEmailColumn = (email) => {
    if (!hasFetchedSBAccounts.current || isRefreshingAccounts) return <Spinner variant="dots" classNames={{ "wrapper": "-top-2" }} />

    if (!email) {
      return (
        <>
          <XCircleIcon className="inline-block w-6 h-6 mr-2 text-orange-500" /> 
          <span className="text-gray-500">{user.me.client.settings.is_sb_account_sso ? "Awaiting first login" : "No SB Account Associated"}</span>
        </>
      ) 
    }

    return <><CheckCircleIcon className="inline-block w-6 h-6 mr-2 text-green-500" /> {email}</>
  }
  
  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    
    Promise.all([
      refetch(),
      refreshAccounts()
    ]).finally(() => {
      setRefetching(false)
    })
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getSiteDataStaffXlsDownloadLink()
    }
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleShowLessons = staffMember => {
    setCurrentStaffMember(staffMember)
    setShowLessonsList(true)
  }

  const handleShowSBAccountSelector = staffMember => {
    setCurrentStaffMember(staffMember)
    setShowSBAccountSelector(true)
  }

  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilter => {
    setSelectedFilters([...selectedFilter])
    setPage(1)
  }

  const renderCell = (staffMember, column) => {
    if (column === "actions") {
      const items = [
        { id: 1, handleClick: () => handleShowLessons(staffMember), caption: "View Lessons" },
      ]

      if (user?.me?.client?.settings?.allow_manual_sb_account_matching) {
        items.push({ id: 2, handleClick: () => handleShowSBAccountSelector(staffMember), caption: "SB Account Selector" })
      }

      return <EllipsisDropdown items={items} />
    }

    if (column === "sb_account_email") {
      const sbAccountEmail = getSBAccountEmail(staffMember?.user?.email)
      return getSBAccountEmailColumn(sbAccountEmail)
    }
    
    if (column === "is_teacher") {
      return staffMember.is_teacher ? "Yes" : "No"
    }

    if (column === "reg_group") {
      return staffMember["reg_group"] || "-"
    }

    if (["created_at", "updated_at"].includes(column)) {
      return mySqlToLocal(staffMember[column]).format("YYYY-MM-DD HH:mm")
    }

    if (column === "is_sync" && staffMember.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync", content: "text-white" }} size="sm" variant="flat">
          Sync
        </Chip>
      )
    }

    if (column === "is_sync" && !staffMember.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync-custom", content: "text-white" }} size="sm" variant="flat">
          Custom
        </Chip>
      )
    }

    return staffMember[column]
  }

  if (error) return `Error! ${error.message}`

  const showLoadingPlaceholder = showLoader(loading, networkStatus, data?.client?.staff?.data, refetching);

  return (
    <Page>
      <PageHeader
        title="Staff Data"
        subTitle="Imported staff data from your MIS / CSV file."
        icon={<CircleStackIcon className="page-header-icon" />}
        contentBottom={
          <div className="flex flex-wrap gap-2">
            <IntegrationTypeBadge name={user?.me?.client?.importType?.name || "unknown"} />
            <MisBadge name={user?.me?.client?.mis || "n/a"} />
            <MisCountBadge name={`${summaryData?.data?.dataStaffSummary?.total || 0} Staff`} />
          </div>
        }
        actions={<SiteDataMenu clientId={user.me.client.id} />}
      />

      <Stats
        summary={{
          dataAge: summaryData?.data?.dataStaffSummary?.data_age,
          lastImport: lastImportDuration(summaryData?.data?.dataStaffSummary?.imported_at),
          total: summaryData?.data?.dataStaffSummary?.total,
          syncCount: summaryData?.data?.dataStaffSummary?.sync_count,
          customCount: summaryData?.data?.dataStaffSummary?.custom_count,
          missingLessonCount: summaryData?.data?.dataStaffSummary?.missing_lesson_count,
        }}
      />

      <DataTable
        columns={columns}
        currentPage={page}
        currentSortOrder={sortDescriptor}
        data={data?.client?.staff?.data}
        disallowEmptyFilterSelection={false}
        downloadOptions={downloadOptions}
        filters={filters}
        filtersSelectionMode="single"
        handleDownload={type => handleDownload(type)}
        handlePageChange={page => setPage(page)}
        handleRefetch={() => handleRefetchButtonClicked()}
        handleRenderCell={(item, key) => renderCell(item, key)}
        handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
        handleSearchClear={handleSearchClear}
        handleSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
        handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
        handleToggleFilter={selectedFilter => handleToggleFilter(selectedFilter)}
        isDownloading={isDownloading}
        loading={showLoadingPlaceholder}
        isRefetching={refetching}
        noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Staff Found" description="No Staff found! Try clearing any filters." />}
        paginatorInfo={data?.client?.staff?.paginatorInfo}
        searchInputPlaceHolder="Search by forename / surname / display name / email"
        searchTerm={searchTerm}
        selectedColumns={selectedColumns}
        selectedFilters={selectedFilters}
        showFilterCount={true}
        showDownloadButton={true}
        showRefetchButton={true}
      />    

      <FlyoutMenu 
        isOpen={showLessonsList}
        title={currentStaffMember ? staffFullname(currentStaffMember) : "Lessons List"}
        subTitle={currentStaffMember ? `${staffFullname(currentStaffMember)} lessons are listed below.` : "Lessons are listed below."}
        onClose={() => setShowLessonsList(false)}
      >
        {showLessonsList && (
          <StaffLessonsList staffMember={currentStaffMember} />
        )}
      </FlyoutMenu>

      <FlyoutMenu 
        isOpen={showSBAccountSelector}
        title={currentStaffMember ? staffFullname(currentStaffMember) : "SB Account Selector"}
        subTitle={currentStaffMember ? `${staffFullname(currentStaffMember)} SB Account Details.` : "SB Account Details."}
        onClose={() => setShowSBAccountSelector(false)}
      >
        {showSBAccountSelector && (
          <SBAccountSelector 
            client={user.me.client} 
            staffMember={currentStaffMember}  
            sbAccounts={sbAccounts}
            sbAccountEmail={getSBAccountEmail(currentStaffMember?.user?.email)}
            handleClose={() => setShowSBAccountSelector(false)}
            handleRefreshSBAccounts={refreshAccounts}
            handleUpdateSBAccounts={setSbAccounts}
            isRefreshingSBAccounts={isRefreshingAccounts}
          />
        )}
      </FlyoutMenu>
    </Page>
  )
}

export default Staff